import React, { useState } from 'react';

import { c } from 'ttag';

import { Badge, Button } from '@freelancelabs/components';

import Card from '../../components/Card';
import { PortalUserQuery } from '../../graphql/__generated__/types';
import GrantDashboardDialog from './GrantDashboardDialog';
import RevokeDashboardDialog from './RevokeDashboardDialog';
import { blockedNotice, inactiveAccessSentence } from './dashboards';
import { EMPTY, formatDate } from './display';
import { statusCategory, statusLabel } from './status';

type Membership = NonNullable<NonNullable<PortalUserQuery['portalUser']>['memberships']>[number];
type Granted = Membership['dashboards'][number];

interface Props {
    memberships: Membership[];
    personName: string;
}

// Dashboards grouped by the membership each hangs from: an habilitation is held by a
// membership, not a person, and two accounts may declare an entry of the same label, so a
// flat list could not say which account a row is granted under. Blocked entries stay
// visible (they answer "who had it"), each naming which of the three reasons applies.
const DashboardsCard = ({ memberships, personName }: Props) => {
    const [granting, setGranting] = useState<string | null>(null);
    // Hold the entry whole, not an id: withdrawing deletes the row, so an id would resolve
    // to nothing when the refetch lands and the dialog would vanish mid-confirmation.
    const [revoking, setRevoking] = useState<{ membershipId: string; entry: Granted } | null>(null);

    const total = memberships.reduce((sum, membership) => sum + (membership.dashboards ?? []).length, 0);

    const grantingMembership = memberships.find((membership) => membership.membershipId === granting);
    const revokingMembership = memberships.find((membership) => membership.membershipId === revoking?.membershipId);

    return (
        <>
            <Card
                title={c('User dashboards').t`Dashboards`}
                // This list has no pages, so its length is the complete set.
                counter={total}
            >
                <div className="flex flex-col gap-6">
                    {memberships.map((membership) => {
                        const dashboards = membership.dashboards ?? [];
                        const membershipId = membership.membershipId;
                        const inactive = inactiveAccessSentence(membership.status);

                        return (
                            <div key={membershipId}>
                                <div className="flex flex-wrap items-center gap-2">
                                    <h3 className="text-sm font-semibold text-text-900">
                                        {membership.organizationName}
                                    </h3>
                                    {membership.status && (
                                        <Badge category={statusCategory(membership.status)} size="small">
                                            {statusLabel(membership.status)}
                                        </Badge>
                                    )}
                                    <Button
                                        className="ml-auto"
                                        category="weak"
                                        size="small"
                                        disabled={!membershipId}
                                        onClick={() => setGranting(membershipId ?? null)}
                                    >
                                        {c('User dashboards').t`Grant a dashboard…`}
                                    </Button>
                                </div>

                                {inactive && <p className="mt-1.5 text-xs text-text-600">{inactive}</p>}

                                {dashboards.length === 0 ? (
                                    <p className="mt-1.5 text-xs text-text-600">
                                        {c('User dashboards')
                                            .t`No dashboard is granted on this account. That is the ordinary state rather than a gap: the catalogue is a whitelist entered by hand, and nothing is granted until somebody grants it.`}
                                    </p>
                                ) : (
                                    <ul className="mt-3 flex flex-col gap-3">
                                        {dashboards.map((dashboard) => {
                                            const blocked = blockedNotice(dashboard.blockedReason);
                                            // Computed before the template: `ttag` refuses a call
                                            // expression inside a localized string.
                                            const grantedOn = formatDate(dashboard.grantedAt);
                                            const grantedBy = dashboard.grantedByName || EMPTY;
                                            return (
                                                <li
                                                    key={dashboard.dashboardId}
                                                    className="flex flex-wrap items-start gap-x-3 gap-y-1.5 border-b border-border-weak pb-3 last:border-b-0 last:pb-0"
                                                >
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex flex-wrap items-center gap-2">
                                                            <span className="text-sm font-medium text-text-900">
                                                                {dashboard.name}
                                                            </span>
                                                            {blocked ? (
                                                                <Badge category={blocked.tone} size="small">
                                                                    {blocked.tag}
                                                                </Badge>
                                                            ) : (
                                                                // Only where the access is active: a
                                                                // green "opens" over a closed access
                                                                // would contradict the sentence above.
                                                                !inactive && (
                                                                    <Badge category="success" size="small">
                                                                        {c('User dashboards').t`Opens`}
                                                                    </Badge>
                                                                )
                                                            )}
                                                        </div>
                                                        {blocked && (
                                                            <p className="mt-1 text-xs text-text-600">
                                                                {blocked.sentence}
                                                            </p>
                                                        )}
                                                        <p className="mt-1 text-xs text-text-500">
                                                            {c('User dashboards')
                                                                .t`Granted on ${grantedOn} by ${grantedBy}`}
                                                        </p>
                                                    </div>
                                                    <Button
                                                        category="weak"
                                                        size="small"
                                                        disabled={!membershipId}
                                                        onClick={() =>
                                                            setRevoking({
                                                                membershipId: membershipId as string,
                                                                entry: dashboard,
                                                            })
                                                        }
                                                    >
                                                        {c('User dashboards').t`Withdraw`}
                                                    </Button>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </div>
                        );
                    })}
                </div>
            </Card>

            {grantingMembership?.membershipId && grantingMembership.organizationId && (
                <GrantDashboardDialog
                    membershipId={grantingMembership.membershipId}
                    organizationId={grantingMembership.organizationId}
                    organizationName={grantingMembership.organizationName ?? EMPTY}
                    personName={personName}
                    heldDashboardIds={(grantingMembership.dashboards ?? []).map((entry) => entry.dashboardId)}
                    inactiveAccess={inactiveAccessSentence(grantingMembership.status)}
                    onClose={() => setGranting(null)}
                />
            )}

            {revoking && revokingMembership && (
                <RevokeDashboardDialog
                    membershipId={revoking.membershipId}
                    organizationName={revokingMembership.organizationName ?? EMPTY}
                    personName={personName}
                    entry={revoking.entry}
                    onClose={() => setRevoking(null)}
                />
            )}
        </>
    );
};

export default DashboardsCard;
